import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, RefreshCw, CheckCircle, XCircle, Loader, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const SYNC_TYPE_LABELS = {
  conges_config_to_solde: '📅 Sync config congés → solde',
  conges_retroactifs_calc: '📊 Recalcul jours utilisés',
  unite_rangement_to_team_lead: '🚐 Sync unité de rangement',
  user_role_sync: '🔑 Sync rôle utilisateur',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-CA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export default function SyncFailuresPage() {
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [includeResolved, setIncludeResolved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/sync-failures`, {
        params: { include_resolved: includeResolved },
      });
      setFailures(res.data?.failures || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [includeResolved]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (failure, action) => {
    setBusyId(failure.id);
    try {
      const res = await axios.post(`${API_URL}/api/admin/sync-failures/${failure.id}/${action}`);
      const labels = { retry: 'Sync relancée avec succès', resolve: 'Marqué résolu', dismiss: 'Ignoré' };
      toast.success(labels[action] || 'OK');
      await load();
      return res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Erreur lors de l'action ${action}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="page-sync-failures">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Synchronisations échouées
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Suivi des post-traitements secondaires qui ont planté en silence (calculs de congés, sync rôles, etc.).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-600 flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={includeResolved}
              onChange={(e) => setIncludeResolved(e.target.checked)}
              data-testid="checkbox-include-resolved"
            />
            Voir aussi les résolus
          </label>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="btn-refresh-failures">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <Loader className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : failures.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <p className="text-emerald-700 font-semibold">Aucune sync en échec 🎉</p>
            <p className="text-xs text-slate-500 mt-1">
              {includeResolved
                ? "Pas d'historique d'échecs."
                : "Tout roule en arrière-plan."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {failures.map(f => (
            <Card key={f.id} data-testid={`failure-card-${f.id}`} className={f.resolved ? 'opacity-60' : ''}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800 text-sm">
                        {SYNC_TYPE_LABELS[f.sync_type] || f.sync_type}
                      </span>
                      {f.failure_count > 1 && (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 text-[10px]">
                          ✕{f.failure_count} échecs
                        </Badge>
                      )}
                      {f.resolved && (
                        <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-[10px]">
                          ✓ {f.resolution_method === 'auto_retry' ? 'Auto-retry' : f.resolution_method === 'dismissed' ? 'Ignoré' : 'Résolu'}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {f.entity_label}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Dernier échec : {formatDate(f.last_failed_at)}
                      </span>
                    </div>
                    <div className="mt-2 bg-red-50 border border-red-200 rounded px-2 py-1.5 text-[11px] text-red-800 font-mono break-all">
                      {f.error_message || '(pas de message)'}
                    </div>
                    {f.context && Object.keys(f.context).length > 0 && (
                      <div className="mt-1 text-[10px] text-slate-400">
                        Contexte : {Object.entries(f.context).map(([k, v]) => `${k}=${v}`).join(', ')}
                      </div>
                    )}
                  </div>

                  {!f.resolved && (
                    <div className="flex gap-1 flex-wrap shrink-0">
                      {f.retry_supported && (
                        <Button
                          size="sm"
                          className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleAction(f, 'retry')}
                          disabled={busyId === f.id}
                          data-testid={`btn-retry-${f.id}`}
                        >
                          {busyId === f.id ? <Loader className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                          Relancer
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-emerald-400 text-emerald-700"
                        onClick={() => handleAction(f, 'resolve')}
                        disabled={busyId === f.id}
                        data-testid={`btn-resolve-${f.id}`}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Résolu
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-slate-500"
                        onClick={() => handleAction(f, 'dismiss')}
                        disabled={busyId === f.id}
                        data-testid={`btn-dismiss-${f.id}`}
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Ignorer
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
